/**
 * ЗЕРКАЛО реестра названий юридических документов — ПОЛНЫЙ НАБОР (29 языков).
 *
 * Правда — `backend/src/ai-funnels/legal-doc-names.registry.ts`: тот же набор
 * названий нужен ОБЕИМ сторонам, потому что дубль названий в строке согласия
 * снимается дважды и по одному правилу — на генерации (пост-ген-гард WEB-896)
 * и на рендере, где автор ввёл текст руками (WEB-901). Разойдись половины —
 * генерация и ручной ввод дадут на публике разный результат, и никто этого не
 * заметит. Кабинету нельзя импортировать из бэкенда (его нет в сборке),
 * поэтому данные скопированы, а совпадение МНОЖЕСТВ в обе стороны держит
 * сторож `backend/src/shared/guards/legal-doc-names-ends.web2384.spec.ts`.
 *
 * ПОЧЕМУ ОТДЕЛЬНО ОТ `legalDocNames.public.ts` — вес публичного входа
 * (временное решение 23.09.2026, разобрано в докблоке публичной части): в
 * браузер покупателя едут только английский и русский, и выражения там
 * предвычислены. Импорт ЭТОГО файла и есть добор: он подменяет источник
 * выражений полным набором. Кабинет делает это на своём входе
 * (`src/main.tsx`) — тем же приёмом, что и языковые пакеты строк воронки
 * (WEB-2057): публичный вход не платит за то, чем пользуется только кабинет.
 *
 * ФОРМА ЗАПИСИ — ОДНА СТРОКА НА ЯЗЫК: пять групп через «;» (однозначные
 * названия условий, голые формы условий, однозначные названия политики, голые
 * формы политики, соединители), варианты внутри группы через «|», образец
 * распознавания после «=» там, где голого текста мало (русские и украинские
 * падежи). Соединитель «&» общий для всех языков и в данных не хранится.
 *
 * Названия документов — не надписи кабинета, а образцы РАСПОЗНАВАНИЯ текста
 * воронки на её языке; правило двух языков (словари ru/en) к ним не относится.
 */
import {
  installLegalDocPatterns,
  type LegalDocLanguage,
  type LegalDocName,
  type LegalDocPatterns,
} from './legalDocNames.public';

/**
 * Буква/цифра — граница «слова». `\b` тут врёт: он знает только латиницу.
 *
 * WEB-2384: к латинице и кириллице добавлены письменности языков генерации,
 * КОТОРЫЕ РАЗДЕЛЯЮТ СЛОВА ПРОБЕЛОМ — латинские расширения (ç, ü, ș, đ),
 * греческий, иврит, арабский, деванагари. Без этого граница слова в греческой
 * или ивритской воронке ничего не значила: буква чужой письменности считалась
 * разделителем, и название матчилось посреди слова.
 *
 * Письменностей без пробелов (ханьцзы, кана, хангыль, тайская) тут намеренно
 * НЕТ: в «利用規約とプライバシーポリシーに同意します» соседняя буква — не признак
 * другого слова, и требование границы просто не дало бы найти название вовсе.
 * Для них работает само однозначное название целиком.
 */
export const LEGAL_WORD_CHAR =
  'A-Za-z0-9' +
  '\\u00C0-\\u024F' + // латиница с диакритикой
  '\\u0370-\\u03FF' + // греческий
  '\\u0400-\\u04FF' + // кириллица
  '\\u0590-\\u05FF' + // иврит
  '\\u0600-\\u06FF' + // арабский
  '\\u0900-\\u097F' + // деванагари
  '\\u1E00-\\u1EFF'; // латиница доп. (вьетнамский)

/** Соединитель, общий для всех языков, — в строках языков не хранится. */
const SHARED_CONNECTOR = '&';

/**
 * Строки языков. Региональные варианты (pt-BR, zh-CN) своей строки не имеют:
 * они разрешаются падением на базовый язык — кроме zh-TW, где письменность
 * действительно другая.
 */
/** Строки публичного набора — те же, что предвычислены в `legalDocNames.public.ts`. */
const LEGAL_DOC_PUBLIC_ROWS: Readonly<Record<string, string>> = {
  en:
    'Terms of Use|Terms of Service|Terms and Conditions;Terms;Privacy Policy;Privacy;and',
  ru:
    'Условия использования=услови[а-яё]*\\s+использования|Пользовательское соглашение=пользовательск[а-яё]*\\s+соглашени[а-яё]*;Условия=услови[а-яё]*;Политика конфиденциальности=политик[а-яё]*\\s+конфиденциальност[а-яё]*;Политика=политик[а-яё]*|Конфиденциальность=конфиденциальност[а-яё]*;и', // i18n-allow: образцы РАСПОЗНАВАНИЯ авторского текста воронки (её язык), не надписи кабинета
};

/**
 * Остальные языки генерации — только там, где веса по проводу не считают:
 * кабинет, редактор, предпросмотр, тесты, сторожа.
 */
const LEGAL_DOC_EXTRA_ROWS: Readonly<Record<string, string>> = {
  es:
    'Términos de uso|Términos y condiciones|Condiciones de uso;Términos|Condiciones;Política de privacidad;Privacidad;y|e',
  pt:
    'Termos de uso|Termos de serviço;Termos;Política de privacidade;Privacidade;e',
  de:
    'Nutzungsbedingungen|Allgemeine Geschäftsbedingungen|AGB;Bedingungen;Datenschutzerklärung|Datenschutzrichtlinie;Datenschutz;und',
  fr:
    'Conditions d\'utilisation|Conditions générales;Conditions;Politique de confidentialité;Confidentialité;et',
  it:
    'Termini di servizio|Termini d\'uso|Termini e condizioni;Termini;Informativa sulla privacy|Politica sulla privacy;Privacy;e|ed',
  zh:
    '使用条款|服务条款;条款;隐私政策;隐私;和|与',
  'zh-TW':
    '使用條款|服務條款;條款;隱私政策;隱私;和|與',
  ja:
    '利用規約|サービス利用規約;規約;プライバシーポリシー;プライバシー;と|および',
  ko:
    '이용약관|서비스 약관;약관;개인정보처리방침|개인정보 처리방침;개인정보;및|와|과',
  ar:
    'شروط الاستخدام|شروط الخدمة;الشروط;سياسة الخصوصية;الخصوصية;و',
  he:
    'תנאי השימוש|תנאי שימוש;התנאים;מדיניות הפרטיות|מדיניות פרטיות;הפרטיות;ו',
  hi:
    'उपयोग की शर्तें|सेवा की शर्तें;शर्तें;गोपनीयता नीति;गोपनीयता;और',
  tr:
    'Kullanım Koşulları|Kullanım Şartları|Hizmet Şartları;Koşullar|Şartlar;Gizlilik Politikası;Gizlilik;ve',
  pl:
    'Warunki korzystania|Regulamin;Warunki;Polityka prywatności;Prywatność;i|oraz',
  nl:
    'Gebruiksvoorwaarden|Algemene voorwaarden;Voorwaarden;Privacybeleid|Privacyverklaring;Privacy;en',
  sv:
    'Användarvillkor|Användningsvillkor;Villkor;Integritetspolicy;Integritet;och',
  da:
    'Brugsbetingelser|Servicevilkår;Betingelser|Vilkår;Privatlivspolitik;Privatliv;og',
  no:
    'Bruksvilkår|Brukervilkår;Vilkår;Personvernerklæring;Personvern;og',
  fi:
    'Käyttöehdot|Palveluehdot;Ehdot;Tietosuojakäytäntö|Tietosuojaseloste;Tietosuoja;ja',
  cs:
    'Podmínky použití|Obchodní podmínky;Podmínky;Zásady ochrany osobních údajů;Soukromí;a',
  el:
    'Όροι Χρήσης|Όροι Παροχής Υπηρεσιών;Όροι;Πολιτική Απορρήτου;Απόρρητο;και',
  uk:
    'Умови використання=умов[а-яіїєґ]*\\s+використання|Користувацька угода=користувацьк[а-яіїєґ]*\\s+угод[а-яіїєґ]*;Умови=умов[а-яіїєґ]*;Політика конфіденційності=політик[а-яіїєґ]*\\s+конфіденційност[а-яіїєґ]*;Політика=політик[а-яіїєґ]*|Конфіденційність=конфіденційност[а-яіїєґ]*;і|та|й', // i18n-allow: образцы РАСПОЗНАВАНИЯ авторского текста воронки (её язык), не надписи кабинета
  ro:
    'Termeni și condiții|Condiții de utilizare|Termeni de utilizare;Termeni|Condiții;Politica de confidențialitate;Confidențialitate;și',
  hu:
    'Felhasználási feltételek|Általános szerződési feltételek;Feltételek;Adatvédelmi irányelvek|Adatvédelmi tájékoztató;Adatvédelem;és',
  th:
    'ข้อกำหนดการใช้งาน|เงื่อนไขการใช้บริการ;ข้อกำหนด|เงื่อนไข;นโยบายความเป็นส่วนตัว;ความเป็นส่วนตัว;และ',
  vi:
    'Điều khoản sử dụng|Điều khoản dịch vụ;Điều khoản;Chính sách bảo mật;Bảo mật;và',
  id:
    'Syarat Penggunaan|Ketentuan Layanan|Syarat dan Ketentuan;Syarat|Ketentuan;Kebijakan Privasi;Privasi;dan',
};

function parseNames(group: string): LegalDocName[] {
  if (group === '') return [];
  return group.split('|').map((item) => {
    const at = item.indexOf('=');
    return at === -1
      ? { text: item }
      : { text: item.slice(0, at), pattern: item.slice(at + 1) };
  });
}

function parseLanguage(row: string): LegalDocLanguage {
  const [termsFull, termsBare, privacyFull, privacyBare, connectors] =
    row.split(';');
  return {
    terms: { full: parseNames(termsFull), bare: parseNames(termsBare) },
    privacy: { full: parseNames(privacyFull), bare: parseNames(privacyBare) },
    connectors: [
      ...(connectors === '' ? [] : connectors.split('|')),
      SHARED_CONNECTOR,
    ],
  };
}

/** Реестр по коду языка — разобранные строки выше, второго списка нет. */
export const LEGAL_DOC_NAMES: Record<string, LegalDocLanguage> =
  Object.fromEntries(
    Object.entries(LEGAL_DOC_PUBLIC_ROWS).map(([code, row]) => [
      code,
      parseLanguage(row),
    ]),
  );

/**
 * Добор языков в живой реестр. Кэш собранных выражений сбрасывается: вызов до
 * добора закрепил бы неполный набор.
 */
export function registerLegalDocRows(
  extra: Readonly<Record<string, string>>,
): void {
  for (const [code, row] of Object.entries(extra)) {
    LEGAL_DOC_NAMES[code] = parseLanguage(row);
  }
  PATTERN_CACHE.clear();
}

/** Экранирование литерала для подстановки в регулярное выражение. */
function escapeForRegex(text: string): string {
  return text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/** Образец распознавания названия: свой, если задан, иначе выведенный. */
export function legalDocNamePattern(name: LegalDocName): string {
  if (name.pattern !== undefined) return name.pattern;
  return escapeForRegex(name.text).replace(/\s+/g, '\\s+');
}

/**
 * Записи реестра для языка воронки. Английский и русский подмешиваются ВСЕГДА:
 * модель нередко оставляет английские подписи в неанглийской воронке (шрам
 * WEB-785), а до WEB-2384 резались только они — снимать это поведение нельзя.
 * Незнакомый код языка просто не добавляет ничего своего.
 */
export function legalDocLanguagesFor(
  language?: string | null,
): LegalDocLanguage[] {
  const normalized = (language ?? '').trim().toLowerCase().replace(/_/g, '-');
  const keys = Object.keys(LEGAL_DOC_NAMES);
  const exact = keys.find((key) => key.toLowerCase() === normalized);
  const base = keys.find(
    (key) => key.toLowerCase() === normalized.split('-')[0],
  );
  const picked = exact ?? base;

  const entries: LegalDocLanguage[] = [
    LEGAL_DOC_NAMES.en,
    LEGAL_DOC_NAMES.ru,
  ];
  if (picked !== undefined && picked !== 'en' && picked !== 'ru') {
    entries.push(LEGAL_DOC_NAMES[picked]);
  }
  return entries;
}

const PATTERN_CACHE = new Map<string, LegalDocPatterns>();

/**
 * Регулярные выражения для языка воронки — строятся ИЗ РЕЕСТРА, руками нигде
 * не набраны. Зеркало на другой половине строит их той же формулой.
 */
export function buildLegalDocPatterns(language?: string | null): LegalDocPatterns {
  const key = (language ?? '').trim().toLowerCase();
  const cached = PATTERN_CACHE.get(key);
  if (cached) return cached;

  const entries = legalDocLanguagesFor(language);
  const alt = (parts: string[]): string =>
    // Длинные варианты раньше коротких: иначе «Условия использования»
    // совпало бы только голым «Условия», без хвоста.
    [...new Set(parts)].sort((a, b) => b.length - a.length).join('|');

  const fullTerms = entries.flatMap((e) =>
    e.terms.full.map(legalDocNamePattern),
  );
  const bareTerms = entries.flatMap((e) =>
    e.terms.bare.map(legalDocNamePattern),
  );
  const fullPrivacy = entries.flatMap((e) =>
    e.privacy.full.map(legalDocNamePattern),
  );
  const barePrivacy = entries.flatMap((e) =>
    e.privacy.bare.map(legalDocNamePattern),
  );
  const connectors = entries.flatMap((e) =>
    e.connectors.map((c) => escapeForRegex(c)),
  );

  const fullName = `(?:${alt([...fullTerms, ...fullPrivacy])})`;
  const bareName = `(?:${alt([...bareTerms, ...barePrivacy])})`;
  const docName = `(?:${fullName}|${bareName})`;
  const connector = `(?:\\s*(?:${alt(connectors)})\\s*)`;

  const patterns: LegalDocPatterns = {
    docRun: new RegExp(
      `(^|[^${LEGAL_WORD_CHAR}])(${docName}(?:${connector}${docName})*)(?![${LEGAL_WORD_CHAR}])`,
      'gi',
    ),
    hasAnchor: new RegExp(fullName, 'i'),
    taggedDocName: new RegExp(
      `(${alt([...fullTerms, ...bareTerms])})|(${alt([...fullPrivacy, ...barePrivacy])})`,
      'gi',
    ),
  };

  PATTERN_CACHE.set(key, patterns);
  return patterns;
}

/**
 * Плоский список названий для ПОДСТРОЧНОГО поиска по надписи
 * (`strip-duplicate-legal-links.util.ts`). Второй копии списка нет — он
 * выводится отсюда обходом реестра.
 */
export function legalDocPlainNames(): string[] {
  const out = new Set<string>();
  for (const entry of Object.values(LEGAL_DOC_NAMES)) {
    for (const kind of [entry.terms, entry.privacy]) {
      for (const name of [...kind.full, ...kind.bare]) out.add(name.text);
    }
  }
  return [...out];
}

/**
 * Добор всех языков генерации и подмена источника выражений.
 *
 * Зовётся при загрузке модуля — тогда редактор, предпросмотр, тесты и сторожа
 * получают полный набор без порядка загрузки на честном слове, — и отдельным
 * вызовом из `src/main.tsx`, чтобы импорт не выглядел лишним.
 */
export function registerAllLegalDocLanguages(): void {
  registerLegalDocRows(LEGAL_DOC_EXTRA_ROWS);
  installLegalDocPatterns(buildLegalDocPatterns);
}

registerAllLegalDocLanguages();

/** Явный список доборных языков — для сторожа и для чтения глазами. */
export const EXTRA_LEGAL_DOC_LANGUAGES: readonly string[] =
  Object.keys(LEGAL_DOC_EXTRA_ROWS);

/**
 * Языки, которые работают у покупателя БЕЗ языкового пакета, — ядро первого
 * экрана. Объявлены здесь, а не в публичном модуле: там каждое имя платное, а
 * нужен список только сторожу и тестам.
 */
export const PUBLIC_LEGAL_DOC_LANGUAGES: readonly string[] = ['en', 'ru'];
export { buildLegalDocPatterns as legalDocPatterns };
export type { LegalDocName, LegalDocLanguage, LegalDocPatterns };
